import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from './useAuth';

export type Notification = {
  id: string;
  type: 'idea_update' | 'new_idea' | 'store_link' | 'new_poll' | 'new_milestone' | 'milestone_update';
  data: {
    idea_id: string;
    idea_title: string;
    title?: string;
    author_id?: string;
    old_status?: string;
    new_status?: string;
    store_link?: string;
    status?: string;
  };
  read: boolean;
  created_at: string;
};

export function useNotifications() {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  useEffect(() => {
    let mounted = true;

    if (user) {
      // Wait for auth to be ready before fetching
      const fetchData = async () => {
        try {
          const session = await supabase.auth.getSession();
          if (!session.data.session) {
            throw new Error('No active session');
          }
          
          if (mounted) {
            await fetchNotifications();
            subscribeToNotifications();
          }
        } catch (error) {
          console.error('Error initializing notifications:', error);
          setLoading(false);
        }
      };

      fetchData();
    } else {
      setNotifications([]);
      setUnreadCount(0);
      setLoading(false);
    }

    return () => {
      mounted = false;
    };
  }, [user]);

  const fetchNotifications = async () => {
    if (!user) return;

    try {
      // Use the user_notifications view to get non-deleted notifications
      const { data, error } = await supabase
        .from('user_notifications')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(50);

      if (error) throw error;

      setNotifications(data || []);
      setUnreadCount((data || []).filter(n => !n.read).length);
    } catch (error) {
      console.error('Error fetching notifications:', error);
      setNotifications([]);
      setUnreadCount(0);
    } finally {
      setLoading(false);
    }
  };

  const subscribeToNotifications = () => {
    if (!user) return;

    const subscription = supabase
      .channel('notifications')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${user.id}`
        },
        (payload) => {
          setNotifications(prev => [payload.new as Notification, ...prev]);
          setUnreadCount(prev => prev + 1);
        }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  };

  const markAsRead = async (notificationId: string) => {
    if (!user) return;

    try {
      const { error } = await supabase
        .from('notifications')
        .update({ read: true })
        .eq('id', notificationId)
        .eq('user_id', user.id);

      if (error) throw error;

      setNotifications(prev =>
        prev.map(n =>
          n.id === notificationId ? { ...n, read: true } : n
        )
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const markAllAsRead = async () => {
    if (!user) return;

    try {
      const { error } = await supabase
        .from('notifications')
        .update({ read: true })
        .eq('user_id', user.id)
        .eq('read', false);

      if (error) throw error;

      setNotifications(prev =>
        prev.map(n => ({ ...n, read: true }))
      );
      setUnreadCount(0);
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  };

  const clearNotification = async (notificationId: string) => {
    if (!user) return;

    try {
      // Instead of deleting the notification, create a deletion record
      const { error } = await supabase
        .from('notification_deletions')
        .insert({
          user_id: user.id,
          notification_id: notificationId
        });

      if (error) throw error;

      // Update local state
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
      setUnreadCount(prev => 
        prev - (notifications.find(n => n.id === notificationId)?.read ? 0 : 1)
      );
    } catch (error) {
      console.error('Error clearing notification:', error);
    }
  };

  return {
    notifications,
    unreadCount,
    loading,
    markAsRead,
    markAllAsRead,
    clearNotification
  };
}